import axios from "axios";
import { message } from "antd";
import { initializeKeycloak, keycloakInstance } from "./keycloack";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

axiosInstance.interceptors.request.use(async (config) => {
  const authenticated = await initializeKeycloak();

  if (!authenticated) {
    return config;
  }
  await keycloakInstance.updateToken(30);

  config.headers.Authorization = `Bearer ${keycloakInstance.token}`;

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  function (error) {
    const errorMsg = error.response.data ?? "axios.server.error";

    if (error.response.status === 403) {
      message.error("Permessi insufficienti", 5);
    } else {
      message.error("Errore: " + (error.response?.data?.message ?? "axios.server.error"), 5);
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;

const crud = (path) => ({
  list: (params) => axiosInstance.get(path, { params }).then(({ data }) => data),
  create: (data) => axiosInstance.post(path, data).then(({ data }) => data),
  update: (id, data) => axiosInstance.put(path + "/" + id, data).then(({ data }) => data),
  delete: (id) => axiosInstance.delete(path + "/" + id).then(({ data }) => data),
  get: (id) => axiosInstance.get(path + "/" + id).then(({ data }) => data),
});

export const api = {
  document: crud("document"),
  tag: crud("tag"),
  vehicleSetupCurrentInspection: {
    list: (params) =>
      axiosInstance.get("vehicleSetupCurrentInspection", { params }).then(({ data }) => data),
  },
  dpiCurrentInspection: {
    list: (params) =>
      axiosInstance.get("dpiCurrentInspection", { params }).then(({ data }) => data),
  },
  equipmentCurrentInspection: {
    list: (params) =>
      axiosInstance.get("equipmentCurrentInspection", { params }).then(({ data }) => data),
  },
  inspection: {
    ...crud("inspection"),
    create: (data) =>
      axiosInstance
        .post("inspection", data, { headers: { "Content-Type": "multipart/form-data" } })
        .then(({ data }) => data),
  },
  absenceType: crud("absenceType"),
  absence: crud("absence"),
  equipment: crud("equipment"),
  equipmentType: crud("equipmentType"),
  vehicleType: crud("vehicleType"),
  setupType: crud("setupType"),
  workSite: {
    ...crud("workSite"),
    getPosView: (id) => axiosInstance.get("workSite/" + id + "/posView").then(({ data }) => data),
  },
  vehicle: crud("vehicle"),
  vehicleCurrentInspection: {
    list: (params) =>
      axiosInstance.get("vehicleCurrentInspection", { params }).then(({ data }) => data),
  },
  vehicleSetup: crud("vehicleSetup"),
  equipmentAssignment: {
    ...crud("equipmentAssignment"),
    unassign: (id, data) => axiosInstance.post(`equipmentAssignment/${id}/unassign`, data),
  },
  posRequest: {
    ...crud("posRequest"),
    claimRequest: (id) =>
      axiosInstance.post("posRequest/" + id + "/claim").then(({ data }) => data),
  },
  pos: crud("pos"),
  posModel: {
    ...crud("posModel"),
    create: (data) =>
      axiosInstance
        .post("posModel", data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(({ data }) => data),
  },
  employee: crud("employee"),
  employeeCurrentMedicalFitness: {
    list: (params) =>
      axiosInstance.get("employeeCurrentMedicalFitness", { params }).then(({ data }) => data),
  },
  dpi: crud("dpi"),
  employeeRequiredDpi: {
    list: (params) => axiosInstance.get("employeeRequiredDpi", { params }).then(({ data }) => data),
  },
  dpiProduct: crud("dpiProduct"),
  appointment: crud("appointment"),
  appointmentType: crud("appointmentType"),
  dpiInventoryUnit: crud("dpiInventoryUnit"),
  course: crud("course"),
  employeeRequiredCourse: {
    list: (params) =>
      axiosInstance.get("employeeRequiredCourse", { params }).then(({ data }) => data),
  },
  courseType: crud("courseType"),
  doctor: crud("doctor"),
  medicalVisit: crud("medicalVisit"),
  workActivity: crud("workActivity"),
  mansion: crud("mansion"),
  dpiTransaction: crud("dpiTransaction"),
  docExport: {
    export: (data) => axiosInstance.post("docExport", data),
  },
};
